import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../config/api';
import { deviceType, getVisitorId, postAnalytics } from '../utils/analytics';
import '../styles/card.css';

function initialsFor(user) {
  const first = (user.first_name || '').trim().charAt(0);
  const last = (user.last_name || '').trim().charAt(0);
  const fallback = (user.full_name || '?').trim().charAt(0);
  return (first + last || fallback).toUpperCase();
}

function displayWebsite(url) {
  return String(url || '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
}

function IconPhone() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1L6.6 10.8z"
      />
    </svg>
  );
}

function IconSend() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"
      />
    </svg>
  );
}

function IconBriefcase() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M10 4h4a2 2 0 0 1 2 2v1h4a2 2 0 0 1 2 2v3H2V9a2 2 0 0 1 2-2h4V6a2 2 0 0 1 2-2zm0 3h4V6h-4v1zM2 14h20v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5z"
      />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm7.4 9h-3.1a15.4 15.4 0 0 0-1.3-6 8.02 8.02 0 0 1 4.4 6zM12 4c.9 1.3 1.6 3.1 1.9 5h-3.8C10.4 7.1 11.1 5.3 12 4zM4.6 13a8.02 8.02 0 0 1 0-2h3.1a15.7 15.7 0 0 0 0 2H4.6zm1 2h3.1a15.4 15.4 0 0 0 1.3 6 8.02 8.02 0 0 1-4.4-6zM12 20c-.9-1.3-1.6-3.1-1.9-5h3.8c-.3 1.9-1 3.7-1.9 5zm5.3 1a15.4 15.4 0 0 0 1.3-6h3.1a8.02 8.02 0 0 1-4.4 6zM16.3 13a17.7 17.7 0 0 0 0-2h3.1a8.02 8.02 0 0 1 0 2h-3.1zM7.4 4a8.02 8.02 0 0 0-4.4 6h3.1a15.4 15.4 0 0 1 1.3-6z"
      />
    </svg>
  );
}

function IconPersonAdd() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M15 12c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zM6 8V6H4v2H2v2h2v2h2v-2h2V8H6zm9 6c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z"
      />
    </svg>
  );
}

function Row({ href, icon, value, label }) {
  if (!value) return null;
  const inner = (
    <>
      <span className="card-row-icon">{icon}</span>
      <span className="card-row-text">
        <span className="card-row-value">{value}</span>
        <span className="card-row-label">{label}</span>
      </span>
    </>
  );
  if (href) {
    return (
      <a className="card-row" href={href}>
        {inner}
      </a>
    );
  }
  return <div className="card-row">{inner}</div>;
}

function Avatar({ card, uuid }) {
  const [broken, setBroken] = useState(false);
  // The API streams the headshot from the photo bucket; employees without one
  // 404, and the onError path drops back to initials in the same visual slot.
  const photoSrc = card.photo_url || (uuid ? API.photo(uuid) : null);

  if (photoSrc && !broken) {
    return (
      <img
        className="card-avatar-img"
        src={photoSrc}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <div className="card-avatar" aria-hidden="true">
      {initialsFor(card)}
    </div>
  );
}

export default function CardPage() {
  const { uuid } = useParams();
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState('loading');

  // Lock page scroll — card must fit one iPhone viewport (FAB overlays content).
  useEffect(() => {
    document.documentElement.classList.add('card-route');
    return () => document.documentElement.classList.remove('card-route');
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(API.card(uuid));
        if (res.status === 404) {
          if (!cancelled) setStatus('missing');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setCard(data);
          setStatus('ready');
          document.title = data.full_name ? `${data.full_name} | Ryan Companies` : 'Ryan Companies';
          postAnalytics(API.cardView(uuid), {
            event_type: 'card_view',
            visitor_id: getVisitorId(),
            device_type: deviceType(),
            referrer: document.referrer || null,
          });
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  // Absolute vCard URL so mobile Safari gets a navigable .vcf (Add Contact).
  const vcardHref = useMemo(() => (uuid ? API.vcard(uuid) : '#'), [uuid]);
  const showContactFab = useMemo(() => deviceType() !== 'desktop', []);

  if (status === 'loading') {
    return <p className="card-status">Loading card…</p>;
  }
  if (status === 'missing') {
    return (
      <div className="card-status-block">
        <img src="/shamrock-green.png" alt="" className="card-status-logo" />
        <p>This business card could not be found.</p>
      </div>
    );
  }
  if (status === 'error') {
    return <p className="card-status">Something went wrong loading this card.</p>;
  }

  const telHref = card.phone ? `tel:${String(card.phone).replace(/[^\d+]/g, '')}` : null;
  const mailHref = card.email ? `mailto:${card.email}` : null;
  const webHref = card.website || null;

  return (
    <div className="card-page">
      <article className="card">
        <Avatar card={card} uuid={uuid} />
        <h1 className="card-name">{card.full_name}</h1>
        {card.title ? <p className="card-role">{card.title}</p> : null}

        <div className="card-actions">
          {telHref ? (
            <a className="card-action" href={telHref}>
              <IconPhone />
              CALL
            </a>
          ) : null}
          {mailHref ? (
            <a className="card-action" href={mailHref}>
              <IconSend />
              EMAIL
            </a>
          ) : null}
        </div>

        <div className="card-list">
          <Row href={telHref} icon={<IconPhone />} value={card.phone} label="Telephone" />
          <Row href={mailHref} icon={<IconMail />} value={card.email} label="Email" />
          <Row
            icon={<IconBriefcase />}
            value={card.company || 'Ryan Companies US, Inc.'}
            label={card.title || 'Company'}
          />
          <Row
            href={webHref}
            icon={<IconGlobe />}
            value={displayWebsite(webHref)}
            label="Website"
          />
        </div>

        {showContactFab ? (
          <a
            className="card-fab"
            href={vcardHref}
            download
            aria-label="Save contact"
            onClick={() => {
              postAnalytics(API.cardView(uuid), {
                event_type: 'vcard_download',
                visitor_id: getVisitorId(),
                device_type: deviceType(),
                referrer: document.referrer || null,
              });
            }}
          >
            <IconPersonAdd />
          </a>
        ) : null}
      </article>
    </div>
  );
}
